/* assets/sperrgut.js
 * LED-Leisten nach Maß: ab window.sperrgutConfig.minLengthCm (Easify-Feld "Profillänge")
 * bekommt der Artikel die versteckte Eigenschaft `_Sperrgut: ja`. Shipfy wertet sie im Checkout aus,
 * auch bei PayPal Express. Feste Sperrgutprodukte erkennt Shipfy direkt am Varianten-Metafeld
 * custom.sperrgut; dafür braucht es hier keinen Code.
 *
 *  1. Produktformular: die Eigenschaft steht schon im Formular, bevor "In den Warenkorb"
 *     oder ein Express-Button auf der Produktseite gedrückt wird.
 *  2. Warenkorb: Fallback für Leisten, die ohne die Eigenschaft in den Warenkorb kamen.
 *  3. Übergang: das alte Warenkorb-Attribut `_sperrgut` wird weiter gepflegt, bis die neue
 *     Shipfy-Regel aktiv ist. Danach kann Abschnitt 3 samt Kollektions-Abfrage entfallen.
 */
(() => {
  const config = Object.assign(
    { collectionHandle: 'aluminium-led-profile', minLengthCm: 118 },
    window.sperrgutConfig || {}
  );

  const PROP_KEY = '_Sperrgut';
  const PROP_VALUE = 'ja';
  const LENGTH_KEY = /^_?Profill(ä|ae)nge$/i;
  const LENGTH_FIELD = /^properties\[_?Profill(ä|ae)nge\]$/i;

  // Eigene Anfragen laufen über das Original-fetch, damit sie den Interceptor nicht erneut auslösen.
  const originalFetch = window.fetch.bind(window);

  // ---------- Länge auslesen ----------
  // Easify-Werte sehen z. B. so aus: "x: 100 cm (€32,00)" oder "120,5 cm".
  const parseLengthCm = (value) => {
    if (value == null) return null;
    const text = String(value);
    const cm = text.match(/(\d+(?:[.,]\d+)?)\s*cm/i);
    const m = !cm && text.match(/(\d+(?:[.,]\d+)?)\s*m\b/i);
    const any = !cm && !m && text.match(/\d+(?:[.,]\d+)?/);
    const raw = (cm && cm[1]) || (m && m[1]) || (any && any[0]);
    if (!raw) return null;
    const n = parseFloat(raw.replace(',', '.'));
    if (!Number.isFinite(n)) return null;
    return m ? n * 100 : n;
  };

  const isLongEnough = (value) => {
    const cm = parseLengthCm(value);
    return cm !== null && cm >= config.minLengthCm;
  };

  // ---------- 1. Produktformular ----------
  const productForms = () =>
    Array.from(document.querySelectorAll('form[action*="/cart/add"]'));

  const syncForm = (form) => {
    let long = false;
    for (const [name, value] of new FormData(form).entries()) {
      if (LENGTH_FIELD.test(name) && isLongEnough(value)) {
        long = true;
        break;
      }
    }

    let input = form.querySelector('input[data-sperrgut-auto]');
    if (long && !input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${PROP_KEY}]`;
      input.value = PROP_VALUE;
      input.setAttribute('data-sperrgut-auto', '');
      form.appendChild(input);
    } else if (!long && input) {
      input.remove();
    }
  };

  const syncAllForms = () => productForms().forEach(syncForm);

  // Easify setzt seine Felder teils per Skript, daher zusätzlich zu den Events ein kurzes Intervall.
  ['input', 'change', 'click', 'keyup'].forEach((type) =>
    document.addEventListener(type, () => setTimeout(syncAllForms, 0), true)
  );
  // Direkt vor dem Absenden synchronisieren (Capture-Phase läuft vor dem Theme-Handler).
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (form && form.matches && form.matches('form[action*="/cart/add"]')) syncForm(form);
    },
    true
  );
  setInterval(syncAllForms, 700);

  // ---------- 3. Übergang: Kollektion für das alte Warenkorb-Attribut ----------
  let sperrgutProductIds = null;

  const loadSperrgutProductIds = async () => {
    if (sperrgutProductIds) return sperrgutProductIds;
    const cacheKey = `sperrgut-ids-${config.collectionHandle}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.t < 30 * 60 * 1000) {
        sperrgutProductIds = new Set(cached.ids);
        return sperrgutProductIds;
      }
    } catch (e) {}

    const ids = [];
    for (let page = 1; page <= 10; page++) {
      const res = await originalFetch(
        `/collections/${config.collectionHandle}/products.json?limit=250&page=${page}`
      );
      if (!res.ok) break;
      const data = await res.json();
      const products = data.products || [];
      products.forEach((p) => ids.push(p.id));
      if (products.length < 250) break;
    }
    sperrgutProductIds = new Set(ids);
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), ids }));
    } catch (e) {}
    return sperrgutProductIds;
  };

  // ---------- 2. Warenkorb ----------
  const itemIsLongStrip = (item) => {
    const props = item.properties || {};
    return Object.keys(props).some((k) => LENGTH_KEY.test(k) && isLongEnough(props[k]));
  };

  const postJson = (url, body) =>
    originalFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  let running = null;
  let rerun = false;

  const checkCart = async () => {
    const cart = await (await originalFetch('/cart.js')).json();
    const ids = await loadSperrgutProductIds();
    let cartHasSperrgut = false;
    let itemsChanged = false;

    for (const item of cart.items) {
      const needs = itemIsLongStrip(item);
      const props = item.properties || {};
      const has = props[PROP_KEY] === PROP_VALUE;
      if (needs || has || ids.has(item.product_id)) cartHasSperrgut = true;

      // Nur ergänzen, nie entfernen.
      if (needs && !has) {
        const newProps = Object.assign({}, props, { [PROP_KEY]: PROP_VALUE });
        await postJson('/cart/change.js', { id: item.key, quantity: item.quantity, properties: newProps });
        itemsChanged = true;
      }
    }

    // Geänderte Artikel bekommen neue Zeilen-Keys, daher Warenkorb-Anzeige neu laden.
    if (itemsChanged) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.reload === 'function') drawer.reload();
      else if (document.querySelector('.section-main-cart')) window.location.reload();
    }

    // ---------- 3. Übergang: altes Warenkorb-Attribut (für die bisherige Shipfy-Regel) ----------
    const flag = cartHasSperrgut ? 'true' : 'false';
    if ((cart.attributes || {})._sperrgut !== flag) {
      await postJson('/cart/update.js', { attributes: { _sperrgut: flag } });
    }
    return cartHasSperrgut;
  };

  // Läuft nie doppelt; Änderungen während eines Laufs lösen genau einen weiteren Lauf aus.
  const scheduleCheck = () => {
    if (running) {
      rerun = true;
      return running;
    }
    running = checkCart()
      .catch((e) => console.error('[sperrgut]', e))
      .finally(() => {
        running = null;
        if (rerun) {
          rerun = false;
          scheduleCheck();
        }
      });
    return running;
  };

  // Alle Warenkorb-Änderungen des Themes und der Apps abfangen.
  window.fetch = function (...args) {
    const result = originalFetch(...args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(() => scheduleCheck()).catch(() => {});
    }
    return result;
  };

  // Vor dem normalen Checkout warten, bis die Prüfung fertig ist.
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('a[href="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      scheduleCheck().then(() => {
        window.location.href = '/checkout';
      });
    },
    true
  );

  const init = () => {
    syncAllForms();
    scheduleCheck();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
